<!--
  CollectorLineStatus — what a line says once it is not simply «markable»
  (collector-app P1-D, handoff spec Section 2.5): the leader's rejection, the
  after-mark chip with «ارجّع», «راجعه الليدر ✓ · مقفول», «لاغي», a live member
  question (until she answers). Read from the line's own flags (collector/lineView.js `lineState`),
  never recomputed. `compact` is a member row of a grouped card and the marked
  single card: «ارجّع» only — what the mark came to is said beside it
  (CollectorLineResult), which also carries the verified chip.
-->
<template>
  <div v-if="state === 'marked' && (!compact || line.canReopen)" class="c-status c-status--row">
    <span
      v-if="!compact"
      class="c-chip c-status__chip"
      :class="line.outcome === 'collected' ? 'c-chip--green' : 'c-chip--red'"
    >
      {{ markedChip }}
    </span>
    <button
      v-if="line.canReopen"
      type="button"
      class="c-reset c-status__reopen"
      @click="emit('reopen', line)"
    >
      {{ t('collector.line.reopen') }}
    </button>
  </div>
  <div v-else-if="state === 'verified' && !compact" class="c-status c-status--row">
    <span class="c-chip c-chip--lilac">{{ t('collector.line.verified') }}</span>
  </div>
  <div v-else-if="state === 'released'" class="c-status c-status--row">
    <span class="c-chip c-chip--grey">{{ t('collector.line.released') }}</span>
  </div>
  <div v-else-if="state === 'question' && !compact && !answered" class="c-status c-status--note">
    {{ questionText }}
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { formatClock } from '@/collector/dayView.js';
import { answerView, lineState } from '@/collector/lineView.js';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  line: { type: Object, required: true },
  compact: { type: Boolean, default: false },
});
const emit = defineEmits(['reopen']);

const { t } = useI18n();
const { formatAmount } = useCurrency();
const pounds = (cents) => formatAmount(cents, { trimZeros: true });

const state = computed(() => lineState(props.line));
/** Once she answered, «مستنيين ردّها» would be false: her answer block says the rest. */
const answered = computed(() => answerView(props.line)?.answered === true);

/** «اتجاب ✓ · لقيت 2 من 3 · 320 ج.م» / «مش موجود» */
const markedChip = computed(() => {
  const { line } = props;
  if (line.outcome !== 'collected') return t('collector.line.oosChip');
  const parts = [t('collector.line.collectedChip')];
  const found = line.collectorFacts?.foundQty;
  if (found != null && Number(found) !== Number(line.qty)) {
    parts.push(t('collector.line.foundOf', { found, qty: line.qty }));
  }
  if (line.collectorFacts?.priceCents != null) {
    parts.push(t('collector.line.price', { price: pounds(line.collectorFacts.priceCents) }));
  }
  return parts.join(' · ');
});

const questionText = computed(() => {
  const time = formatClock(props.line.activeQuestion?.sentAt);
  return time ? t('collector.line.question', { time }) : t('collector.line.questionNoTime');
});
</script>

<style scoped>
.c-status--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.c-status__chip {
  flex: 0 1 auto;
  min-width: 0;
  padding: 6px 12px;
  font-size: 13px;
  white-space: normal;
}

.c-status__reopen {
  flex-shrink: 0;
  height: 44px;
  padding: 0 14px;
  border: 1px solid var(--c-border);
  border-radius: var(--c-radius-chip);
  background: var(--c-ground);
  color: var(--c-indigo);
  font-size: 14px;
  font-weight: 700;
}

.c-status--note {
  padding: 8px 12px;
  border-radius: var(--c-radius-chip);
  background: var(--c-lilac);
  color: var(--c-indigo);
  font-size: 13px;
  font-weight: 700;
}
</style>
